import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { TelegramQR } from '../components/TelegramQR';

interface Stats {
  sessions?: { total_sessions: number; total_tokens: number; avg_heal_loops: number; successful: number; failed: number };
  top_users?: Array<{ username: string; session_count: number; tokens: number }>;
}

const ENDPOINT_URL = (import.meta.env.VITE_API_BASE ?? window.location.origin) + '/v1';

export function DashboardPage() {
  const [stats, setStats] = useState<Stats>({});
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // API Keys state
  const [keys, setKeys] = useState<any[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [revealed, setRevealed] = useState<{ name: string; key: string } | null>(null);
  const [copiedBaseUrl, setCopiedBaseUrl] = useState(false);
  const [copiedNewKey, setCopiedNewKey] = useState(false);

  const loadKeys = () => {
    api.listApiKeys()
      .then((d: any) => setKeys(d.api_keys ?? []))
      .catch((e) => console.error('Failed to load API keys:', e))
      .finally(() => setLoadingKeys(false));
  };

  useEffect(() => {
    Promise.all([
      api.getStats().catch(() => ({})),
      api.getProfile().catch(() => null),
      api.listApiKeys().catch(() => ({ api_keys: [] }))
    ]).then(([statsRes, userRes, keysRes]) => {
      setStats(statsRes);
      if (userRes?.user) setUser(userRes.user);
      setKeys(keysRes.api_keys ?? []);
    }).finally(() => {
      setLoading(false);
      setLoadingKeys(false);
    });
  }, []);

  const createKey = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const result = await api.createApiKey(newKeyName.trim());
      setRevealed(result);
      setNewKeyName('');
      loadKeys();
    } catch (e) {
      alert(String(e));
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (id: string, prefix: string) => {
    if (!confirm(`Revoke key ${prefix}••••••••? This cannot be undone.`)) return;
    try {
      await api.revokeApiKey(id);
      loadKeys();
    } catch (e) {
      alert(String(e));
    }
  };

  const copyBaseUrl = () => {
    navigator.clipboard.writeText(ENDPOINT_URL);
    setCopiedBaseUrl(true);
    setTimeout(() => setCopiedBaseUrl(false), 2000);
  };

  const copyNewKey = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedNewKey(true);
    setTimeout(() => setCopiedNewKey(false), 2000);
  };

  const s = stats.sessions;

  const STAT_CARDS = [
    { label: 'Token Balance',   value: loading ? '…' : user ? formatNum(Number(user.tokens_balance)) : '0', icon: '💰', color: '#34d399' },
    { label: 'Total Sessions',  value: loading ? '…' : formatNum(s?.total_sessions ?? 0), icon: '💬', color: '#a78bfa' },
    { label: 'Tokens Generated',value: loading ? '…' : formatNum(s?.total_tokens ?? 0),   icon: '⚡', color: '#22d3ee' },
    { label: 'Success Rate',    value: loading ? '…' : s ? `${Math.round((s.successful / Math.max(s.total_sessions, 1)) * 100)}%` : '0%', icon: '✅', color: '#10b981' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Ericoder AI Software Factory</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid-2" style={{ gap: '16px' }}>
        {STAT_CARDS.map(c => <StatCard key={c.label} {...c} />)}
      </div>

      {/* Client Setup + Telegram */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Client Setup / OpenAI Endpoint */}
        <div className="card card--accent">
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🔌</span> Client Setup &amp; OpenAI Endpoint
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Base URL</div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#a78bfa', background: 'rgba(124,58,237,0.1)', padding: '6px 10px', borderRadius: '6px', flex: 1, wordBreak: 'break-all' }}>
                  {ENDPOINT_URL}
                </code>
                <button className="btn btn--secondary btn--sm" onClick={copyBaseUrl}>
                  {copiedBaseUrl ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Auth header</div>
              <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#22d3ee', background: 'rgba(6,182,212,0.1)', padding: '6px 10px', borderRadius: '6px', display: 'block', wordBreak: 'break-all' }}>
                Authorization: Bearer sk-eri-...
              </code>
            </div>
          </div>
          <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
            Use model name <code style={{ fontFamily: 'var(--font-mono)', color: '#a78bfa' }}>ericoder</code> or any Ericoder model alias.<br/>
            In Cursor: Settings → Models → Add Custom Model.
          </div>
        </div>

        {/* Telegram QR */}
        <TelegramQR />
      </div>

      {/* Revealed key modal */}
      {revealed && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <div className="card" style={{ maxWidth: '480px', width: '100%', border: '1px solid var(--border-accent)', background: 'var(--bg-surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '20px' }}>🔑</span>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>API Key Created: {revealed.name}</div>
            </div>
            <div className="api-key-reveal" style={{ marginBottom: '12px' }}>{revealed.key}</div>
            <div className="key-warning" style={{ marginBottom: '16px' }}>⚠️ Save this key now — it will never be shown again.</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn--primary" onClick={() => copyNewKey(revealed.key)} style={{ flex: 1, justifyContent: 'center' }}>
                {copiedNewKey ? '✓ Copied!' : 'Copy Key'}
              </button>
              <button className="btn btn--secondary" onClick={() => setRevealed(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Create New API Key */}
      <div className="card">
        <div className="card__title">Create New API Key</div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            className="input"
            placeholder="Key name (e.g. Cursor Dev, VS Code)"
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createKey()}
            style={{ flex: 1 }}
          />
          <button className="btn btn--primary" onClick={createKey} disabled={creating || !newKeyName.trim()}>
            {creating ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '+ Generate'}
          </button>
        </div>
      </div>

      {/* Your API Keys list */}
      <div className="card">
        <div className="card__title">Your API Keys</div>
        {loadingKeys ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}><div className="spinner" /></div>
        ) : !keys.length ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>
            No API keys yet. Create one above.
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Last Used</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id}>
                    <td style={{ fontWeight: 500 }}>{k.name}</td>
                    <td>
                      <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#a78bfa' }}>
                        {k.key_prefix}••••••••
                      </code>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      {k.last_used ? new Date(k.last_used).toLocaleString() : 'Never'}
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      {new Date(k.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <button className="btn btn--danger btn--sm" onClick={() => revokeKey(k.id, k.key_prefix)}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top users (Admin only) */}
      {(stats.top_users?.length ?? 0) > 0 && (
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '14px' }}>👤 Top Users</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {stats.top_users!.map((u, i) => (
              <div key={u.username} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: '6px' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', width: '16px' }}>#{i + 1}</span>
                <span style={{ flex: 1, fontSize: '13px', fontWeight: 600 }}>{u.username}</span>
                <span className="badge badge--blue">{u.session_count} sessions</span>
                <span className="badge badge--purple">{formatNum(u.tokens)} tok</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, color }: { label: string; value: string | number; icon: string; color: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value" style={{ color }}>{value}</div>
      <div className="stat-card__icon">{icon}</div>
    </div>
  );
}

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return n.toLocaleString('en-US');
}
